<template>
  <popup
    :opened="dataToShow"
    @close="close"
  >
    <!-- BEGIN Content modal add competence -->
    <div
      v-if="dataToShow"
      class="flex flex-col h-full"
    >
      <div class="mt-5 mx-5 lg:mt-15 lg:mx-10 2xl:mt-20 2xl:mx-15">
        <h3 class="mb-10">
          Ajouter d'une compétence liée à un métier
        </h3>
        <h6 class="text-indigo-600 mb-8">
          Saissisez le nom du métier pour obtenir ses compétences
        </h6>
        <div
          class="flex flex-col space-y-3 md:flex-row justify-end items-center md:justify-center md:items-end space-x-5"
        >
          <search-bar
            :big="false"
            :has-border="true"
            :has-suggestion="true"
            :inherits="false"
            :is-searching="isSearchingAppellations"
            :value="metier"
            class="lg:w-1/2"
            libelle="Métier"
            name="searchAppelation"
            placeholder="Exemple : “Conducteur de bus“"
            @displaySuggestionsList="
              (state) => {
                displaySuggestionsList(state);
              }
            "
            @input="
              (newValue) => {
                metier = newValue;
              }
            "
          >
            <template
              v-if="isSuggestionsListDisplayed"
              #suggestionsList
              class="rounded-xl"
            >
              <div
                v-for="(appellation, memberIndex) in appellations"
                :key="memberIndex"
                class="cursor-default p-3 text-base font-normal bg-white bg-opacity-0 odd:bg-gray-200 odd:bg-opacity-50 hover:bg-indigo-200 hover:bg-opacity-100 odd:hover:bg-indigo-200 odd:hover:bg-opacity-100"
                @click.stop="selectResult(appellation)"
              >
                {{ appellation.libelle }}
              </div>
            </template>
          </search-bar>

          <action-button
            :uppercase="true"
            bg-color="bg-indigo-600"
            bg-color-square="bg-indigo-600"
            border-color="border-transparent"
            icon-color="text-white"
            libelle="Valider"
            text-color="text-white"
            type-shadow="shadow-indigo"
            @clickAction="displayCompetences()"
          />
        </div>

        <div
          v-if="isCompetencesDisplayed"
          class="mt-10"
        >
          <div class="flex justify-center mb-10">
            <action-button
              v-if="!isSearchingCompetences"
              :is-disabled="selectedCompetences.length === 0"
              :libelle="
                'AJOUTER ' +
                  (selectedCompetences.length > 1 ? 'LES' : 'LA') +
                  ' COMPÉTENCE' +
                  (selectedCompetences.length > 1 ? 'S' : '') +
                  ' SÉLECTIONNÉE' +
                  (selectedCompetences.length > 1 ? 'S' : '')
              "
              :uppercase="true"
              bg-color="bg-pink-600"
              bg-color-square="bg-pink-600"
              border-color="border-transparent"
              icon-color="text-white"
              text-color="text-white"
              type-shadow="shadow-pink"
              @clickAction="addCompetence()"
            />
          </div>

          <div
            v-if="isSearchingCompetences"
            class="flex justify-center mt-10"
          >
            <div
              class="rounded-full w-4 h-4 border-t-2 border-r-2 border-l-0 border-b-0 border-gray-900 border-opacity-25 animate-spin"
            />
          </div>

          <div
            v-for="(listComp, noeud) in competences"
            :key="'noeud' + noeud"
            class="p-0 odd:bg-gray-200 odd:bg-opacity-50"
          >
            <div
              :class="{
                'bg-indigo-200 border-l-2 border-indigo-600':
                  noeudToDisplay === noeud,
              }"
              class="grid grid-cols-12 gap-x-4 items-center h-auto py-2 cursor-default hover:bg-indigo-200"
              @click="displayNoeud(noeud)"
            >
              <div class="col-span-2 justify-self-center handle">
                <icon
                  :class="{
                    'rotate-90': noeudToDisplay === noeud,
                  }"
                  class="text-pink-600 h-3 transform transition-all duration-200"
                  name="chevron-right"
                />
              </div>
              <div class="col-span-6">
                <p class="text-base">
                  {{ listComp.libelle }}
                </p>
              </div>
              <div class="col-span-4 flex space-x-3">
                <p class="text-base italic font-light">
                  {{ listComp.competences.length }} comp<span class="hidden md:inline-block">étences</span>
                </p>
              </div>
            </div>
            <transition-group
              enter-active-class="ease-out duration-300"
              enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to-class="opacity-100 translate-y-0 sm:scale-100"
              leave-active-class="ease-in duration-200"
              leave-class="opacity-100 translate-y-0 sm:scale-100"
              leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div
                v-show="noeudToDisplay === noeud"
                :key="'noeud' + noeud"
                class="bg-white px-10 py-5"
              >
                <div class="grid grid-cols-3 gap-3">
                  <div
                    v-for="(competence, kc) in listComp.competences"
                    :key="'comp' + kc"
                    class="col-span-full lg:col-span-1"
                  >
                    <tag
                      :can-truncate="true"
                      :icon="
                        selectedCompetences.includes(competence)
                          ? 'minus-circle'
                          : 'plus-circle'
                      "
                      :icon-color="
                        selectedCompetences.includes(competence)
                          ? 'text-indigo-600'
                          : 'text-gray-600'
                      "
                      :libelle="competence"
                      :scale-on-hover="true"
                      :theme-color="
                        selectedCompetences.includes(competence)
                          ? 'indigo-2'
                          : 'white'
                      "
                      class="cursor-pointer"
                      icon-size="w-4 h-4"
                      text-font="font-light"
                      text-size="text-sm"
                      @push="chooseCompetence(competence)"
                    />
                  </div>
                </div>
              </div>
            </transition-group>
          </div>

          <div class="flex justify-center mt-10">
            <action-button
              v-if="!isSearchingCompetences"
              border-color="border-transparent"
              text-color="text-white"
              bg-color="bg-pink-600"
              bg-color-square="bg-pink-600"
              icon-color="text-white"
              :libelle="
                'AJOUTER ' +
                  (selectedCompetences.length > 1 ? 'LES' : 'LA') +
                  ' COMPÉTENCE' +
                  (selectedCompetences.length > 1 ? 'S' : '') +
                  ' SÉLECTIONNÉE' +
                  (selectedCompetences.length > 1 ? 'S' : '')
              "
              type-shadow="shadow-pink"
              :is-disabled="selectedCompetences.length === 0"
              @clickAction="addCompetence()"
              :uppercase="true"
            />
          </div>
        </div>
      </div>
      <div class="text-center text-sm font-light italic mt-20">
        Pôle Emploi - Données originales téléchargées via l'API
        <a
          href="https://francetravail.io/produits-partages/catalogue/rome-4-0-competences"
          target="_blank"
        >ROME</a>
      </div>
    </div>
  </popup>
</template>

<script>
import axios from './../../../api';

import Popup from '../../../components/Popup.vue';
import Icon from '../../../components/Icon.vue';
import InputText from '../../../components/Inputs/InputText.vue';
import InputSelect from '../../../components/Inputs/InputSelect.vue';
import DatePicker from '../../../components/Inputs/DatePicker.vue';
import ActionButton from '../../../components/ActionButton.vue';
import SearchBar from './../../../components/Inputs/SearchBar.vue';
import Tag from './../../../components/Tag.vue';

export default {
  name: 'PopupNewCompMetier',

  components: {
    Popup,
    Icon,
    InputText,
    InputSelect,
    DatePicker,
    ActionButton,
    SearchBar,
    Tag,
  },

  props: {
    dataToShow: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      metier: '',
      loadedCompetences: [],
      isCompetencesDisplayed: false,
      isSuggestionsListDisplayed: false,
      appellations: [],
      selectedResult: {},
      selectedCompetences: [],
      noeudToDisplay: '',
      isSearchingAppellations: false,
      isSearchingCompetences: false,
      doneTypingInterval: 500,
    };
  },

  methods: {
    close() {
      this.$emit('close');
      this.selectedCompetences = [];
      this.isCompetencesDisplayed = false;

      this.loadedCompetences = [];
      this.isSuggestionsListDisplayed = false;
      this.appellations = [];
      this.selectedResult = {};
      this.metier = '';
      this.isSearchingAppellations = false;
      this.isSearchingCompetences = false;
    },

    addCompetence() {
      this.$emit('addCompetence', {
        metier: this.selectedResult.libelle,
        list: this.selectedCompetences,
      });
      this.close(false);
    },

    displaySuggestionsList(state) {
      this.isSuggestionsListDisplayed = state;
    },

    displayCompetences() {
      this.loadedCompetences = [];
      this.isCompetencesDisplayed = true;
      this.getCompetences();
    },

    chooseCompetence(comp) {
      if (this.selectedCompetences.includes(comp)) {
        let index = this.selectedCompetences.indexOf(comp);
        if (index !== -1) {
          this.selectedCompetences.splice(index, 1);
        }
        return;
      }
      this.selectedCompetences.push(comp);
    },

    selectResult(appellation) {
      this.selectedResult = appellation;
      this.metier = appellation.libelle;
      this.displaySuggestionsList(false);
    },

    async getApellations() {
      this.isSearchingAppellations = true;
      await axios
        .post('api/poleemploi/jobs/search', {
          search: this.metier,
        })
        .then((response) => {
          this.appellations = response.data?.resultats ?? [];
          this.isSearchingAppellations = false;
        })
        .catch((err) => {
          this.isSearchingAppellations = false;
          console.log(err);
        });
    },

    async getCompetences() {
      this.isSearchingCompetences = true;
      await axios
        .get('api/poleemploi/competences/' + this.selectedResult.metier.code)
        .then((response) => {
          this.loadedCompetences = response.data;
          this.isSearchingCompetences = false;
        })
        .catch((err) => {
          this.isSearchingCompetences = false;
          console.log(err);
        });
    },

    displayNoeud(noeud) {
      if (this.noeudToDisplay === noeud) {
        this.noeudToDisplay = '';
        return;
      }

      this.noeudToDisplay = noeud;
    },
  },

  computed: {
    competences() {
      let ret = {};
      for (const type in this.loadedCompetences) {
        if (this.loadedCompetences[type].length > 0) {
          let readableType;
          if (type === 'competencesMobilisees') {
            readableType = 'Compétences mobilisées';
          } else if (type === 'competencesMobiliseesPrincipales') {
            readableType = 'Compétences mobilisées principales';
          } else if (type === 'competencesEmergentes') {
            readableType = 'Compétences émergentes';
          } else {
            readableType = 'Autres';
          }
          ret[type] = {
            libelle: readableType,
            competences: this.loadedCompetences[type].map((c) => c.libelle),
          };
        }
      }
      return ret;
    },
  },

  watch: {
    metier() {
      //Call the API only after a set amount of time after search has changed to avoid sending unnecessary requests
      clearTimeout(this.typingTimer);
      this.typingTimer = setTimeout(
        this.getApellations,
        this.doneTypingInterval
      );
    },
  },
};
</script>
